/**
 * liveFeed.js — one Firestore listener per query, shared across mounts, kept alive
 * for a while after the last screen lets go.
 *
 * Why: Daily Ops mounts and unmounts constantly. The 🧺 drawer on the ticket board
 * mounts it on every open, and its tabs mount their own lists. Each mount used to
 * attach a fresh onSnapshot, and Firestore bills a listen that is not resumed within
 * ~30 min as a brand-new query — so every open after a quiet spell re-read the whole
 * 60-day list (up to RECENT_QUERY_LIMIT docs), per device, against the shared
 * free-tier quota. A listener that stays attached is billed only for docs that
 * CHANGE, so reopening costs nothing and the data is on screen instantly.
 *
 * A new subscriber gets its own copy of the latest list right away, then every update.
 * An error drops the feed so the next mount starts a clean one (e.g. after sign-out).
 *
 * ── Not while the screen is off ─────────────────────────────────────────────
 * Lingering only pays while the device stays connected. Kitchen tablets and iPads
 * sleep and wake all day, and every listener that reconnects after more than ~30 min
 * is billed as a brand-new query again — so a feed left lingering re-read its whole
 * window on every wake even if nobody reopened its screen (the 60-day list, the
 * Closing template, the checklist logs). So when the page is hidden, feeds with no
 * subscriber stop at once. A screen opened again soon after resumes cheaply anyway:
 * the persistent cache keeps the listen's resume token.
 */

import { debugLog } from './debug';

/** How long a feed outlives its last subscriber — covers a service day. */
export const FEED_LINGER_MS = 8 * 60 * 60 * 1000;

const feeds = new Map();   // key → { subs:Set, list, meta, timer, stop }

// `meta` is only passed on when the feed sent one.
const deliver = (onData, list, meta) => (meta === undefined ? onData(list.slice()) : onData(list.slice(), meta));

const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Stop `f` now — it has no subscribers left. */
function retire(key, f, why = '') {
  debugLog('close', key, why);
  clearTimeout(f.timer);
  f.timer = null;
  if (feeds.get(key) === f) feeds.delete(key);
  if (typeof f.stop === 'function') f.stop();
}

// Hooked once, on the first feed: the screen going off retires every idle feed.
let watchingVisibility = false;
function watchVisibility() {
  if (watchingVisibility || typeof document === 'undefined') return;
  watchingVisibility = true;
  document.addEventListener('visibilitychange', () => {
    if (!pageHidden()) return;
    feeds.forEach((f, key) => { if (!f.subs.size) retire(key, f, 'screen off'); });
  });
}

/**
 * @param {string}   key     identifies the query; equal keys share one listener
 * @param {Function} open    (emit(list, meta?), fail(err)) → unsubscribe; starts the real listener
 * @param {Function} onData  receives a fresh array per update, plus the feed's `meta`
 * @param {Function} [onError]
 * @returns {Function} unsubscribe
 */
export function shareFeed(key, open, onData, onError, lingerMs = FEED_LINGER_MS) {
  watchVisibility();
  let feed = feeds.get(key);
  if (!feed) {
    const f = { subs: new Set(), list: null, meta: undefined, error: null, timer: null, stop: null };
    feeds.set(key, f);
    debugLog('open', key);
    f.stop = open(
      (list, meta) => { f.list = list; f.meta = meta; f.subs.forEach(s => deliver(s.onData, list, meta)); },
      (err) => {
        // Firestore has already closed an errored listener; forget it so the next
        // subscriber opens a new one instead of waiting on a dead feed.
        f.error = err;
        if (feeds.get(key) === f) feeds.delete(key);
        clearTimeout(f.timer);
        f.subs.forEach(s => s.onError && s.onError(err));
      },
    );
    feed = f;
  }
  const f = feed;
  clearTimeout(f.timer);
  f.timer = null;
  const sub = { onData, onError };
  f.subs.add(sub);
  // Warm feed: replay the latest list (async, like a real snapshot callback).
  if (f.list) Promise.resolve().then(() => { if (f.subs.has(sub) && f.list) deliver(onData, f.list, f.meta); });
  // A feed that failed while opening still reports it to this subscriber.
  if (f.error) Promise.resolve().then(() => { if (f.subs.has(sub) && onError) onError(f.error); });

  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    f.subs.delete(sub);
    if (f.subs.size || feeds.get(key) !== f) return;
    if (pageHidden()) { retire(key, f, 'screen off'); return; }   // nothing on screen to linger for
    f.timer = setTimeout(() => {
      if (feeds.get(key) !== f || f.subs.size) return;
      retire(key, f);
    }, lingerMs);
  };
}

/** Test helper: stop and forget every feed. */
export function resetFeeds() {
  feeds.forEach((f) => { clearTimeout(f.timer); if (typeof f.stop === 'function') f.stop(); });
  feeds.clear();
}
